/* Common Imports */

import { styled } from "@mui/system";
import React from "react";

/* Redux & Type Imports */

import { ObjectId } from "bson";
import { NextRouter } from "next/router";

/* Component Imports */

import { Skeleton, SvgIcon, Tooltip, Typography } from "@mui/material";

/* Icon Imports */

import PremiumBadge from "/public/images/badges/verified_premium.svg";

/* Function Imports */

import BusinessProfileTypeString from "../../../../../lib/BusinessProfileTypeString";

/* Styled Components */

const ColumnContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "space-between",
	alignItems: "flex-start",
	height: "100%",
	padding: "0rem 0rem 0rem 0rem",
	gap: "0.25rem",
	overflow: "hidden",
}));

const RowContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "center",
}));

const CustomImg = styled("img")(({ theme }) => ({
	borderRadius: "0.5rem",
	boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.05)",
	height: "3rem",
	width: "3rem",
	[theme.breakpoints.up("md1190")]: {
		height: "3rem",
		width: "3rem",
	},
}));

const Heading = styled(Typography)(({ theme }) => ({
	fontSize: "0.875rem",
	fontWeight: 500,
	cursor: "pointer",
	color: "#6e3037",
	[theme.breakpoints.up("md1190")]: {
		fontSize: "1rem",
	},
}));

const SubHeading = styled(Typography)(({ theme }) => ({
	fontSize: "0.825rem",
	fontWeight: 400,
	color: theme.palette.text.secondary,
	[theme.breakpoints.up("md1190")]: {
		fontSize: "0.875rem",
	},
}));

const SenderDetails = ({
	router,
	loading,
	sender_is_business_profile,
	sender_name,
	sender_type,
	sender_dp_url,
	sender_premium_active,
	sender_slug,
}: {
	router: NextRouter;
	loading: boolean;
	sender_is_business_profile: boolean;
	sender_name: string;
	sender_type: string;
	sender_dp_url: string | ObjectId;
	sender_premium_active: boolean;
	sender_slug: string | ObjectId;
}) => {
	/* Translations */

	const localLang = {
		premium_user:
			router.locale === "ar-AE" ? "مستخدم مميز" : router.locale === "kn-IN" ? "ಪ್ರೀಮಿಯಂ ಬಳಕೆದಾರ" : "Premium User",
		client: router.locale === "ar-AE" ? "عميل" : router.locale === "kn-IN" ? "ಗ್ರಾಹಕ" : "Client",
	};

	/* Premium Tooltip */

	const [openPremiumTooltip, setOpenPremiumTooltip] = React.useState(false);

	if (loading) {
		return (
			<Skeleton
				animation="wave"
				variant="text"
				width="75%"
				height="auto"
			/>
		);
	}

	const profilePicture =
		typeof sender_dp_url === "string" && sender_dp_url.includes("/images/icons/Avatar.svg")
			? "/images/icons/Avatar.svg"
			: `/profile-picture/${sender_dp_url}`;

	return (
		<RowContainer
			sx={{
				justifyContent: "flex-start",
				alignItems: "flex-start",
				gap: "0.75rem",
			}}
		>
			{/* Profile Picture */}

			<CustomImg
				src={profilePicture as string}
				alt={sender_name}
			/>

			<ColumnContainer
				sx={{
					gap: "0rem",
				}}
			>
				<RowContainer
					sx={{
						justifyContent: "flex-start",
						gap: "0.5rem",
					}}
				>
					{/* Title */}

					<Heading
						variant="body2"
						onClick={() => router.push(`${sender_type}s/${sender_slug}`)}
					>
						{sender_name.length > 20 ? sender_name.substring(0, 20) + "..." : sender_name}
					</Heading>

					{/* Premium Badge */}

					{sender_premium_active ? (
						<Tooltip
							open={openPremiumTooltip}
							onClick={() => setOpenPremiumTooltip(true)}
							onClose={() => setOpenPremiumTooltip(false)}
							onOpen={() => setOpenPremiumTooltip(true)}
							title={localLang.premium_user}
						>
							<SvgIcon
								component={PremiumBadge}
								inheritViewBox={false}
								viewBox="0 0 24 24"
								width={24}
								height={24}
								sx={{
									width: "1rem",
									height: "1rem",
								}}
							/>
						</Tooltip>
					) : null}
				</RowContainer>

				{/* Profile Type */}

				<SubHeading variant="body2">
					{sender_is_business_profile ? BusinessProfileTypeString(sender_type, router) : localLang.client}
				</SubHeading>
			</ColumnContainer>
		</RowContainer>
	);
};

export default SenderDetails;
